import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { IMetricsDataModel } from '../../../data-models/metrics.data-model';
import { companyMapAtom } from '../../../services/hooks/useSaveCompanyData';
import { formulaFieldsListLoadable } from '../../../services/state/AdminPanel/FormulaFieldsStateJ';
import { fundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { applyFormulaFields, formulaFieldsForEntity } from '../../../util/formula-fields';
import { getPlainDateString } from '../../../services/queries/MaggieMetricsQueries';
import { currentCurrencyCodeAtom } from '../../../services/state/CurrencyStateJ';
import { TemporalPlainDateFromJsDate } from '../../../util/date-utilts';
import { runMetricsCalculations } from '../components/OverviewTable/MetricsCalculator';
import { filterMetricsTransactionsAndFundDataByFund } from '../services/kpi/KpiCalculator';
import { QuartersBuilder } from '../services/QuartersBuilder';
import { filteredMetricsPublishAtom, metricsResponseByDateAtom } from './MetricsStateJ';
import { selectedViewDatePFJ } from './PageStateJ';

// Shared so unchanged empty results keep referential identity and don't notify subscribers.
const NO_METRICS: IMetricsDataModel[] = [];

/**
 * Previous-quarter metrics for a company view, scoped to the companies and fund basis of the grid's
 * published rows. Under a fund filter they are re-scoped and recomputed from fund-scoped
 * transactions so the quarter-over-quarter comparison uses the same basis as the current quarter.
 *
 * Everything derives from {@link filteredMetricsPublishAtom} — one value carrying rows AND the fund
 * basis that produced them — and the family is keyed by view id alone, so the atom instance is
 * stable across fund selections. Both properties matter: a fund-selection key would hand new
 * instances a live store read while sibling hooks still hold React's committed rows, and a
 * view-sourced fund basis would race ahead of the rows it belongs to (the FD-4341 flashes).
 *
 * Derived rather than memoized per hook instance: `useCurrentViewMetrics` runs in the table, the
 * readiness check and every chart, so a `useMemo` recomputed this once per instance. Sync on
 * purpose — an async atom would hand out a new promise per derivation and re-suspend the page.
 */
export const previousQuarterMetricsFilteredByViewAtom = atomFamily((viewId: number) =>
  atom((get): IMetricsDataModel[] => {
    const { metrics: filteredMetrics, fundIds } = get(filteredMetricsPublishAtom(viewId));
    // Ambient page date, matching what the grid and KPI tiles render.
    const previousQuarterDate = QuartersBuilder.getPreviousQuarterEnd(get(selectedViewDatePFJ));
    const previousQuarterMetrics = get(
      metricsResponseByDateAtom({
        date: TemporalPlainDateFromJsDate(previousQuarterDate),
        currency: get(currentCurrencyCodeAtom),
      })
    ).data?.metrics;

    if (!filteredMetrics.length || !previousQuarterMetrics) {
      return NO_METRICS;
    }

    const includedCompanies = filteredMetrics.reduce((res, m) => res.add(m.companyId), new Set<number>());
    const prevByCompany = previousQuarterMetrics.filter((m) => includedCompanies.has(m.companyId));

    // Reactive read (unlike a store.get) so this atom recomputes when the
    // formula-fields load resolves; passed down explicitly like the maps.
    // Only company formulas evaluate against metrics rows.
    const formulaLoad = get(formulaFieldsListLoadable);
    const formulaFields = formulaFieldsForEntity(
      formulaLoad.state === 'hasData' ? (formulaLoad.data ?? []) : [],
      'company'
    );
    const companyMap = get(companyMapAtom);

    if (fundIds == null) {
      // ASOFDATE() on these rows is the previous quarter's date — the date
      // this dataset is as of — not the page picker's.
      return applyFormulaFields(prevByCompany, formulaFields, (row) => companyMap.get(row.companyId), {
        asOfDate: getPlainDateString(previousQuarterDate),
      });
    }

    // calculateMetrics drops rows whose company is missing, so skip the pass until the map is populated
    if (companyMap.size === 0) {
      return NO_METRICS;
    }

    const fundsMap = get(fundsByIdMapAtom);
    const fundScoped = filterMetricsTransactionsAndFundDataByFund(prevByCompany, new Set(fundIds), fundsMap);
    // mirror the grid pipeline so previous-quarter scalars are recomputed from fund-scoped transactions
    return runMetricsCalculations(
      fundsMap,
      fundScoped,
      [],
      previousQuarterDate,
      [],
      companyMap,
      new Map(),
      formulaFields
    );
  })
);
